import { useState, useCallback, useEffect } from "react";
import * as XLSX from "xlsx";
import { Spin, Button, Tooltip, Space, Table, Tabs, Empty } from "antd";
import {
	DownloadOutlined,
	FullscreenOutlined,
	FullscreenExitOutlined,
	FileExcelOutlined,
	ReloadOutlined,
} from "@ant-design/icons";
import styles from "./index.module.scss";

/**
 * Excel 文件预览组件
 * 使用 SheetJS (xlsx) 解析 Excel 文件
 * 
 * 支持的格式：xls, xlsx, csv, ods
 */
const ExcelViewer = ({
	url,
	fileName,
	showHeader = true,
	showDownload = true,
	showFullscreen = true,
	height = "calc(100vh - 370px)",
	maxRows = 1000, // 最大显示行数，防止大文件卡顿
	onError,
	onLoad,
}) => {
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [workbook, setWorkbook] = useState(null);
	const [sheetNames, setSheetNames] = useState([]);
	const [activeSheet, setActiveSheet] = useState("");
	const [tableData, setTableData] = useState({ columns: [], dataSource: [] });
	const [isFullscreen, setIsFullscreen] = useState(false);

	// 解析 Sheet 数据为表格格式
	const parseSheetData = useCallback((wb, sheetName) => {
		if (!wb || !sheetName) return { columns: [], dataSource: [] };

		const sheet = wb.Sheets[sheetName];
		if (!sheet) return { columns: [], dataSource: [] };

		// 转换为 JSON 数组
		const jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "" });
		console.log("jsonData:", jsonData)

		if (jsonData.length === 0) {
			return { columns: [], dataSource: [] };
		}

		// 第一行作为表头
		const headerRow = jsonData[0] || [];
		const columns = headerRow.map((header, index) => ({
			title: header || `列${index + 1}`,
			dataIndex: `col_${index}`,
			key: `col_${index}`,
			// 取消表格内置省略，交给样式控制，方便长文本换行展示
			minWidth: 150,
			render: (text) => (
				<span title={String(text || "")}>{text || ""}</span>
			),
		}));

		// 数据行（限制最大行数）
		const dataRows = jsonData.slice(1, maxRows + 1);
		const dataSource = dataRows.map((row, rowIndex) => {
			const rowData = { key: rowIndex };
			headerRow.forEach((_, colIndex) => {
				rowData[`col_${colIndex}`] = row[colIndex] ?? "";
			});
			return rowData;
		});

		return { columns, dataSource };
	}, [maxRows]);

	// 加载并解析文件
	const loadDocument = useCallback(async () => {
		if (!url) return;

		setLoading(true);
		setError(null);
		setWorkbook(null);
		setSheetNames([]);
		setActiveSheet("");
		setTableData({ columns: [], dataSource: [] });

		try {
			// 获取文件
			const response = await fetch(url);
			if (!response.ok) {
				throw new Error(`文件加载失败: ${response.status}`);
			}

			const arrayBuffer = await response.arrayBuffer();

			// 使用 xlsx 解析文件
			const wb = XLSX.read(arrayBuffer, { type: "array" });

			setWorkbook(wb);
			setSheetNames(wb.SheetNames);

			// 默认显示第一个 Sheet
			if (wb.SheetNames.length > 0) {
				const firstSheet = wb.SheetNames[0];
				setActiveSheet(firstSheet);
				setTableData(parseSheetData(wb, firstSheet));
			}

			onLoad?.();
		} catch (err) {
			console.error("Excel 文件加载失败:", err);
			setError("文件加载失败，请检查文件格式是否正确");
			onError?.(err);
		} finally {
			setLoading(false);
		}
	}, [url, parseSheetData, onError, onLoad]);

	// URL 变化时重新加载
	useEffect(() => {
		if (url) {
			loadDocument();
		}
	}, [url, loadDocument]);

	// 切换 Sheet
	const handleSheetChange = useCallback((sheetName) => {
		setActiveSheet(sheetName);
		setTableData(parseSheetData(workbook, sheetName));
	}, [workbook, parseSheetData]);

	// 下载文件
	const handleDownload = useCallback(() => {
		if (url) {
			const link = document.createElement("a");
			link.href = url;
			link.download = fileName || url.split("/").pop() || "spreadsheet.xlsx";
			link.target = "_blank";
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
		}
	}, [url, fileName]);

	// 切换全屏
	const toggleFullscreen = useCallback(() => {
		setIsFullscreen((prev) => !prev);
	}, []);

	// 重新加载
	const handleReload = useCallback(() => {
		loadDocument();
	}, [loadDocument]);

	// 无 URL 时显示提示
	if (!url) {
		return (
			<div className={styles.excelViewer} style={{ height }}>
				<div className={styles.placeholder}>
					<FileExcelOutlined className={styles.placeholderIcon} />
					<div className={styles.placeholderText}>请选择 Excel 文件进行预览</div>
				</div>
			</div>
		);
	}

	// 生成 Tabs items
	const tabItems = sheetNames.map((name) => ({
		key: name,
		label: name,
	}));

	return (
		<div
			className={`${styles.excelViewer} ${isFullscreen ? styles.fullscreen : ""}`}
			style={{ height: isFullscreen ? "100vh" : height }}
		>
			{/* 工具栏 */}
			{showHeader && (
				<div className={styles.toolbar}>
					<div className={styles.fileInfo}>
						<FileExcelOutlined style={{ color: "#217346", fontSize: 18 }} />
						<span className={styles.fileName}>
							{fileName || url.split("/").pop() || "Excel 文件预览"}
						</span>
					</div>
					<Space size="small">
						<Tooltip title="刷新">
							<Button
								type="text"
								icon={<ReloadOutlined />}
								onClick={handleReload}
								disabled={loading}
							/>
						</Tooltip>
						{showDownload && (
							<Tooltip title="下载">
								<Button
									type="text"
									icon={<DownloadOutlined />}
									onClick={handleDownload}
								/>
							</Tooltip>
						)}
						{showFullscreen && (
							<Tooltip title={isFullscreen ? "退出全屏" : "全屏"}>
								<Button
									type="text"
									icon={
										isFullscreen ? (
											<FullscreenExitOutlined />
										) : (
											<FullscreenOutlined />
										)
									}
									onClick={toggleFullscreen}
								/>
							</Tooltip>
						)}
					</Space>
				</div>
			)}

			{/* 预览区域 */}
			<div className={styles.content}>
				{loading && (
					<div className={styles.loading}>
						<Spin size="large" tip="正在加载文件..." />
					</div>
				)}

				{error ? (
					<div className={styles.error}>
						<FileExcelOutlined className={styles.errorIcon} />
						<div className={styles.errorText}>{error}</div>
						<Space>
							<Button onClick={handleReload}>重试</Button>
						</Space>
					</div>
				) : (
					<div className={styles.tableWrapper}>
						{/* Sheet 标签页 */}
						{sheetNames.length > 1 && (
							<Tabs
								activeKey={activeSheet}
								onChange={handleSheetChange}
								items={tabItems}
								className={styles.sheetTabs}
								size="small"
							/>
						)}

						{/* 表格 */}
						{tableData.columns.length > 0 ? (
							<Table
								columns={tableData.columns}
								dataSource={tableData.dataSource}
								pagination={{
									pageSize: 50,
									showSizeChanger: true,
									pageSizeOptions: ["20", "50", "100", "200"],
									showTotal: (total) => `共 ${total} 行`,
								}}
								scroll={{ x: "max-content", y: "calc(100% - 100px)" }}
								size="small"
								bordered
								className={styles.dataTable}
							/>
						) : (
							!loading && (
								<Empty
									description="暂无数据"
									className={styles.empty}
								/>
							)
						)}
					</div>
				)}
			</div>
		</div>
	);
};

export default ExcelViewer;






